import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import clsx from 'clsx';
import { AccordionDetails } from './accordion-details';
import { AccordionSummary } from './accordion-summary';
import { ColorPalette } from '../config';
import { toLowerCaseAndSpaceHyphens } from '../../utilities/string-helper';

export interface IAccordionProps {
  /**
   * Expects one child of type AccordionSummary
   * and one child of type AccordionDetails. Other children are ignored.
   */
  children: React.ReactElement[];
  /**
   * The default expansion value.
   */
  defaultExpanded?: boolean;
  /**
   * True if the Accordion is expanded.
   */
  expanded?: boolean;
  /**
   * Callback to be invoked when the expansion panel expands or collapses.
   */
  onChange?: (event: React.ChangeEvent<unknown>, expanded: boolean) => void;
  /**
   * A control rendered in the summary row, immediately left of the expand icon. It has to stop the
   * click from propagating itself, or it toggles the accordion as well.
   */
  summaryAction?: React.ReactNode;
  /**
   * Content orientation inside AccordionDetails
   */
  detailsLayout?: 'row' | 'column';
  className?: string;
  hasErrors?: boolean;
  noCollapse?: boolean;
  disabled?: boolean;
  denseContent?: boolean;
  id?: string;
  sx?: AccordionProps['sx'];
  square?: AccordionProps['square'];
  elevation?: AccordionProps['elevation'];
}

const useStyles = makeStyles<void, 'disabled'>()((_theme: Theme, _params, classes) => ({
    summaryRoot: {
      minHeight: '48px !important',
      [`&.${classes.disabled}`]: {
        opacity: 1,
      },
    },
    accordionRoot: {
      transition: 'background 0.15s ease',
      [`&.${classes.disabled}`]: {
        backgroundColor: 'white',
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          backgroundColor: 'rgba(255, 255, 255, .6)',
          position: 'absolute',
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
          zIndex: 1,
        },
      },
    },
    accordionWithError: {
      background: `${ColorPalette.Crimson}1f`,
    },
    hidden: {
      display: 'none',
    },
    noCollapse: {
      cursor: 'default',
      opacity: '1 !important',
    },
    disabled: {},
    denseContent: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    summaryAction: {
      display: 'flex',
      alignSelf: 'center',
      // The summary row is 48px: a 24px line of text with 12px content margins, which the theme
      // collapses to 0 when expanded. The negative margins let a 44px touch target overflow the
      // content box instead of making the row taller in either state.
      margin: '-12px 0 -12px auto',
    },
  }));

export function Accordion(props: IAccordionProps) {
  const { classes } = useStyles();
  const { children, detailsLayout, expanded, hasErrors = false, noCollapse = false, disabled = false, denseContent = false, summaryAction, ...rest } = props;

  const summary = children.find(c => c.type === AccordionSummary);
  const details = children.find(c => c.type === AccordionDetails);

  const summaryValue = summary?.props?.children && typeof summary.props.children === 'string' ? summary.props.children : '';
  const accordionId = toLowerCaseAndSpaceHyphens(summaryValue);

  return (
    <MuiAccordion
      disabled={disabled}
      classes={{ disabled: classes.disabled }}
      className={clsx(classes.accordionRoot, { [classes.accordionWithError]: hasErrors && !expanded })}
      expanded={expanded}
      id={accordionId}
      {...rest}
    >
      <MuiAccordionSummary
        classes={{ disabled: classes.disabled }}
        className={clsx(classes.summaryRoot, { [classes.noCollapse]: noCollapse })}
        expandIcon={noCollapse ? null : <ExpandIcon />}
        disabled={noCollapse}
        component="div"
      >
        {summary}
        {summaryAction && <span className={classes.summaryAction}>{summaryAction}</span>}
      </MuiAccordionSummary>
      <MuiAccordionDetails classes={{ root: denseContent ? classes.denseContent : '' }} style={{ flexDirection: detailsLayout || 'column' }}>
        {details}
      </MuiAccordionDetails>
    </MuiAccordion>
  );
}
